import React, { useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createLogger } from '../../../utils/logger';

// UI コンポーネントのインポート
import { 
  TimerDisplay,
  QuizFeedback, 
  QuizResultsSummary, 
  TimeUpAlert, 
  ResponsiveContainer, 
  ResponsiveHeader,
  ResponsiveNavigation
} from '../UI';

// 共有コンポーネントのインポート
import { DebugPanel, DebugToggle } from '../shared';

// クイズコンポーネントのインポート
import QuizProgress from './QuizProgress';
import QuestionRenderer from './QuestionRenderer';

// カスタムフックのインポート
import useQuizPlayer from '../../../hooks/quiz/useQuizPlayer';
import useQuizTimer from '../../../hooks/quiz/useQuizTimer';

// ログシステムの初期化
const logger = createLogger('QuizPlayer');

const QuizPlayer = ({ 
  modalContentId, 
  activeSubject, 
  isModalOpen, 
  studentId,
  contentDetails,
  onClose
}) => {
  // ローカル状態
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [debugMode, setDebugMode] = useState(null);  // フローティングボタン制御用
  
  // refs
  const timeUpProcessingRef = useRef(false);
  const autoProgressTimeoutRef = useRef(null);

  // タイマーフック
  const {
    timeRemaining,
    isActive: isTimerActive,
    startTimer,
    stopTimer,
    resetTimer
  } = useQuizTimer();

  // useQuizPlayerフック
  const {
    questions,
    sessionResponses,
    showFeedback,
    showFinalSummary,
    freeNavigation,
    answerRevealMode,
    startTime,
    endTime,
    attemptFinalizedRef,
    handleQuestionSubmit,
    finalizeAttempt,
    startQuiz,
    resetQuiz,
    setShowFeedback,
    setStartTimeOnly,
    forceShowFinalSummary,
    scrollToTop
  } = useQuizPlayer(modalContentId, activeSubject, isModalOpen, studentId, {}, contentDetails);

  // 現在の質問と回答状態
  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswered = !!(currentQuestion && sessionResponses[currentQuestion.id]);

  // 時間切れ処理
  const handleTimeUp = useCallback(() => {
    if (timeUpProcessingRef.current) return;
    timeUpProcessingRef.current = true;
    
    logger.info('時間切れ処理開始');
    logger.debug('freeNavigation:', freeNavigation);
    setIsTimeUp(true);
    stopTimer();
    
    if (freeNavigation) {
      // 自由移動モードの場合：コンテンツ全体の時間切れ
      logger.info('自由移動モード - コンテンツ全体時間切れ');
      
      // すべての未回答問題を時間切れとして記録
      questions.forEach((question, index) => {
        if (!sessionResponses[question.id]) {
          logger.debug(`問題${question.id}を時間切れとして記録`);
          handleQuestionSubmit(index, false, "時間切れです。", 
            question.type === 'sentence-rearrangement' ? question.englishSentence : (question.answer || ""), 
            ""
          );
        }
      });
      
      // 結果確定
      setTimeout(() => {
        finalizeAttempt(true); // 強制確定
        timeUpProcessingRef.current = false;
      }, 1000);
    } else {
      // 一方向移動モードの場合：問題ごとの時間切れ
      logger.info('一方向移動モード - 問題ごと時間切れ');
      
      // 現在の問題を時間切れとして記録
      const question = questions[currentQuestionIndex];
      if (question && !sessionResponses[question.id]) {
        logger.debug(`問題${question.id}を時間切れとして記録`);
        handleQuestionSubmit(currentQuestionIndex, false, "時間切れです。", 
          question.type === 'sentence-rearrangement' ? question.englishSentence : (question.answer || ""), 
          ""
        );
      }
      
      // 最後まとめ表示モードの場合は自動進行
      if (answerRevealMode === 'end') {
        // 🔧 BUG FIX: 最新のcurrentQuestionIndex値を関数型更新で取得
        setCurrentQuestionIndex(currentIndex => {
          logger.debug('[DEBUG] 最終問題判定（リアルタイム値）:', {
            currentQuestionIndex: currentIndex,
            questionsLength: questions.length,
            questionsLengthMinus1: questions.length - 1,
            isLastQuestion: currentIndex === questions.length - 1
          });
          
          if (currentIndex === questions.length - 1) {
            logger.info('最後の問題 - 時間切れによる結果確定');
            setTimeout(() => {
              finalizeAttempt(true); // 🔧 BUG FIX: 時間切れなのでforced=trueに修正
              timeUpProcessingRef.current = false;
            }, 1000);
            return currentIndex; // インデックスは変更しない
          } else {
            logger.info('次の問題に自動進行');
            setTimeout(() => {
              setIsTimeUp(false);
              timeUpProcessingRef.current = false;
              
              // 次の問題のタイマーを開始
              const timerDuration = contentDetails?.timeLimitPerQuestion || 0;
              if (timerDuration > 0) {
                resetTimer();
                setTimeout(() => {
                  startTimer(timerDuration, handleTimeUp);
                }, 100);
              }
            }, 1000);
            
            logger.debug('[DEBUG] インデックス更新:', { currentIndex, next: currentIndex + 1 });
            return currentIndex + 1; // 次の問題に進む
          }
        });
      } else {
        timeUpProcessingRef.current = false;
      }
    }
  }, [currentQuestionIndex, questions, sessionResponses, answerRevealMode, stopTimer, handleQuestionSubmit, finalizeAttempt, freeNavigation, contentDetails, resetTimer, startTimer]);

  // 初期化
  useEffect(() => {
    if (questions.length > 0 && contentDetails && !showFinalSummary && !initialized) {
      logger.info('QuizPlayer初期化開始');
      logger.debug('設定確認:', {
        timeLimitPerQuestion: contentDetails.timeLimitPerQuestion,
        freeNavigation: contentDetails.freeNavigation,
        answerRevealMode: contentDetails.answerRevealMode,
        quickAnswerMode: contentDetails.quickAnswerMode
      });
      
      // クイズ開始
      if (!startTime) {
        logger.info('クイズ開始処理実行');
        startQuiz();
        setStartTimeOnly();
      }
      
      // タイマー開始
      const timerDuration = freeNavigation 
        ? contentDetails.timeLimitPerContent || 0 
        : contentDetails.timeLimitPerQuestion || 0;
      
      if (timerDuration > 0) {
        logger.info('タイマー開始:', `${timerDuration}秒`);
        resetTimer();
        // 遅延を削除して即座に開始
        startTimer(timerDuration, handleTimeUp);
      }
      
      setInitialized(true);
    }
  }, [questions.length, contentDetails, showFinalSummary, startTime, freeNavigation, initialized, handleTimeUp, resetTimer, setStartTimeOnly, startQuiz, startTimer]);

  // showFinalSummary状態変化の監視（全問回答完了時）
  useEffect(() => {
    if (showFinalSummary && freeNavigation) {
      logger.info('自由移動モード - 結果表示画面に移行');
      // タイマー停止
      stopTimer();
    }
  }, [showFinalSummary, freeNavigation, stopTimer]);

  // showFinalSummaryの詳細監視（最小限のログ）
  useEffect(() => {
    if (showFinalSummary) {
      logger.info('結果表示画面への遷移完了');
    }
  }, [showFinalSummary]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      logger.info('QuizPlayer クリーンアップ');
      if (autoProgressTimeoutRef.current) {
        clearTimeout(autoProgressTimeoutRef.current);
      }
      resetTimer();
      resetQuiz();
      setInitialized(false);
    };
  }, [resetTimer, resetQuiz]);

  // 質問提出処理（useQuizPlayerのhandleQuestionSubmitに合わせる）
  const handleQuestionSubmitWrapper = useCallback(async (isCorrect, explanation, correctAnswer, userAnswer) => {
    logger.info('回答提出処理開始');
    logger.debug('回答情報:', {
      isCorrect,
      currentQuestionIndex,
      answerRevealMode,
      freeNavigation,
      isTimeUp,
      quickAnswerMode: contentDetails?.quickAnswerMode
    });
    
    // 自由移動モードで時間切れの場合は回答受付不可
    if (isTimeUp && freeNavigation) {
      logger.warn('コンテンツ全体時間切れのため回答受付不可');
      return;
    }
    
    // タイマー停止（問題ごとの制限の場合）
    if (!freeNavigation) {
      stopTimer();
    }
    
    // useQuizPlayerのhandleQuestionSubmitを呼び出し（引数の順序に注意）
    handleQuestionSubmit(currentQuestionIndex, isCorrect, explanation, correctAnswer, userAnswer);
    
    // 回答後に全問回答完了チェック（自由移動モード用）
    if (freeNavigation) {
      // 現在の回答を含めた更新後の回答状況をシミュレート
      const currentQuestion = questions[currentQuestionIndex];
      const updatedResponses = {
        ...sessionResponses,
        [currentQuestion.id]: { 
          isCorrect, 
          explanation, 
          correctAnswer,
          userAnswer,
          type: currentQuestion.type,
          details: currentQuestion
        }
      };
      
      // 全問回答完了チェック
      const allAnswered = questions.every(q => updatedResponses[q.id]);
      logger.debug('自由移動モード - 全問回答チェック:', {
        totalQuestions: questions.length,
        answeredQuestions: Object.keys(updatedResponses).length,
        allAnswered
      });
      
      if (allAnswered) {
        logger.info('自由移動モード - 全問回答完了！結果表示準備');
        setTimeout(() => {
          // useQuizPlayerのfinalizeAttemptは既に呼ばれているはずなので、
          // ここでは追加で呼ぶ必要はない
          logger.info('全問回答完了 - useQuizPlayerで自動処理されます');
        }, 100);
      }
    } else {
      // 一方向移動モードの場合の自動進行処理
      if (answerRevealMode === 'end') {
        logger.info('最後まとめ表示モード（一方向移動）');
        
        // 自動進行条件チェック
        const quickAnswerMode = contentDetails?.quickAnswerMode || 'standard';
        const shouldAutoProgress = (
          quickAnswerMode === 'quick' &&
          currentQuestionIndex < questions.length - 1 &&
          !isTimeUp
        );
        
        logger.debug('自動進行条件チェック:', {
          answerRevealMode,
          quickAnswerMode,
          currentQuestionIndex,
          totalQuestions: questions.length,
          isLastQuestion: currentQuestionIndex === questions.length - 1,
          isTimeUp,
          shouldAutoProgress,
          answerRevealModeMatch: answerRevealMode === 'end',
          quickAnswerModeMatch: quickAnswerMode === 'quick'
        });
        
        // 最後の問題かチェック
        if (currentQuestionIndex === questions.length - 1) {
          logger.info('最後の問題 - 結果確定準備（一方向移動）');
          
          if (quickAnswerMode === 'quick') {
            logger.info('クイック回答モード - 自動で結果画面に遷移');
            // 自動的にfinalizeAttemptが呼ばれる（既存の動作を維持）
          } else {
            logger.info('標準モード - ユーザーの操作待ち（自動遷移無効）');
            // 自動遷移は行わず、ユーザーの「結果を表示」ボタンクリック待ち
          }
        } else if (shouldAutoProgress) {
          logger.info('クイック回答モード - 自動で次の問題に進行（回答送信後）');
          // クイック回答モードでの自動進行（新機能）
          autoProgressTimeoutRef.current = setTimeout(() => {
            logger.info('自動進行実行 - 次の問題へ');
            setCurrentQuestionIndex(prev => prev + 1);
            setIsTimeUp(false);
            
            // 次の問題のタイマーを開始
            const timerDuration = contentDetails?.timeLimitPerQuestion || 0;
            if (timerDuration > 0) {
              logger.debug('次の問題のタイマー開始:', timerDuration, '秒');
              resetTimer();
              startTimer(timerDuration, handleTimeUp);
            }
          }, 500); // 500ms遅延でUI更新を確保
        } else {
          logger.info('標準モード - 従来の自動進行処理（非クイック回答モード）');
          // 従来の自動進行（非クイック回答モード）
          autoProgressTimeoutRef.current = setTimeout(() => {
            setCurrentQuestionIndex(prev => prev + 1);
            setIsTimeUp(false);
            
            // 次の問題のタイマーを開始
            const timerDuration = contentDetails?.timeLimitPerQuestion || 0;
            if (timerDuration > 0) {
              logger.debug('次の問題のタイマー開始:', timerDuration, '秒');
              resetTimer();
              startTimer(timerDuration, handleTimeUp);
            }
          }, 1000);
        }
      }
    }
  }, [currentQuestionIndex, freeNavigation, isTimeUp, stopTimer, handleQuestionSubmit, answerRevealMode, questions, sessionResponses, contentDetails, resetTimer, startTimer, handleTimeUp]);

  // ナビゲーションハンドラー
  const handlePrevious = useCallback(() => {
    // 時間切れ後は移動禁止（自由移動モードでコンテンツ全体時間切れの場合）
    if (isTimeUp && freeNavigation) {
      logger.warn('コンテンツ全体時間切れのため移動できません');
      return;
    }
    
    if (freeNavigation && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      scrollToTop();
    }
  }, [freeNavigation, currentQuestionIndex, isTimeUp, scrollToTop]);

  const handleNext = useCallback(() => {
    logger.debug('handleNext 呼び出し');
    
    // 時間切れ後は移動禁止（自由移動モードでコンテンツ全体時間切れの場合）
    if (isTimeUp && freeNavigation) {
      logger.warn('コンテンツ全体時間切れのため移動できません');
      return;
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      const canProceed = freeNavigation || currentAnswered || isTimeUp;
      
      if (canProceed) {
        setCurrentQuestionIndex(prev => prev + 1);
        setShowFeedback(false);
        
        // 一方向移動モードの場合のみisTimeUpをリセット
        if (!freeNavigation) {
          setIsTimeUp(false);
        }
        
        scrollToTop();
        
        // 問題ごとの時間制限モードの場合のみタイマーを再開
        if (!freeNavigation) {
          const timerDuration = contentDetails?.timeLimitPerQuestion || 0;
          if (timerDuration > 0) {
            logger.debug('次の問題のタイマー開始:', timerDuration, '秒');
            resetTimer();
            // 遅延を削除して即座に開始
            startTimer(timerDuration, handleTimeUp);
          }
        }
      } else {
        logger.warn('次の問題に進めません - 回答が必要です');
      }
    }
  }, [currentQuestionIndex, questions.length, freeNavigation, currentAnswered, isTimeUp, contentDetails, scrollToTop, setShowFeedback, resetTimer, startTimer, handleTimeUp]);

  const handleNavigateTo = useCallback((index) => {
    // 時間切れ後は移動禁止（自由移動モードでコンテンツ全体時間切れの場合）
    if (isTimeUp && freeNavigation) {
      logger.warn('コンテンツ全体時間切れのため移動できません');
      return;
    }
    
    if (freeNavigation && index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
      scrollToTop();
    }
  }, [freeNavigation, questions.length, isTimeUp, scrollToTop]);

  // クローズ処理
  const handleClose = useCallback(() => {
    resetTimer();
    resetQuiz();
    setCurrentQuestionIndex(0);
    setIsTimeUp(false);
    setInitialized(false);
    if (autoProgressTimeoutRef.current) {
      clearTimeout(autoProgressTimeoutRef.current);
    }
    onClose();
  }, [resetTimer, resetQuiz, onClose]);

  // 結果表示処理
  const handleShowResults = useCallback(() => {
    logger.info('結果表示処理開始');
    logger.debug('現在の回答状況:', {
      totalQuestions: questions.length,
      answeredQuestions: Object.keys(sessionResponses).length,
      allAnswered: questions.length > 0 && questions.every(q => sessionResponses[q.id]),
      sessionResponses: Object.keys(sessionResponses),
      showFinalSummary: showFinalSummary,
      attemptFinalized: attemptFinalizedRef?.current,
      currentQuestionIndex,
      freeNavigation,
      isLastQuestion: currentQuestionIndex === questions.length - 1,
      hasCurrentAnswer: sessionResponses[questions[currentQuestionIndex]?.id]
    });
    
    // 結果表示の条件判定
    const allAnswered = questions.length > 0 && questions.every(q => sessionResponses[q.id]);
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const hasCurrentAnswer = sessionResponses[questions[currentQuestionIndex]?.id];
    
    let canShowResults = false;
    
    if (freeNavigation) {
      // 自由移動モード：全問回答完了時のみ
      canShowResults = allAnswered;
      if (!canShowResults) {
        logger.warn('自由移動モード - 全問回答未完了のため結果表示不可');
        logger.debug('未回答問題:', questions.filter(q => !sessionResponses[q.id]).map(q => q.id));
        return;
      }
    } else {
      // 一方向移動モード：最後の問題で回答済み、または全問回答完了時
      canShowResults = allAnswered || (isLastQuestion && hasCurrentAnswer);
      if (!canShowResults) {
        logger.warn('一方向移動モード - 結果表示条件未満:', {
          allAnswered,
          isLastQuestion,
          hasCurrentAnswer
        });
        return;
      }
    }

    // 既に結果が表示されている場合はそのまま表示
    if (showFinalSummary) {
      logger.info('既に結果画面表示中');
      return;
    }

    // attemptが完了していない場合のみfinalizeAttemptを実行
    if (!attemptFinalizedRef?.current) {
      logger.info('finalizeAttemptを実行');
      finalizeAttempt(false).then((result) => {
        logger.info('finalizeAttempt完了:', result);
      }).catch((error) => {
        logger.error('finalizeAttemptエラー:', error);
      });
    } else {
      logger.warn('既にattemptが完了済み - showFinalSummaryを確認中');
      // 状態が不整合の場合は強制的にshowFinalSummaryをtrueにする
      if (!showFinalSummary) {
        logger.warn('showFinalSummary状態を強制修正');
        forceShowFinalSummary();
      }
    }
  }, [finalizeAttempt, questions, sessionResponses, showFinalSummary, forceShowFinalSummary, currentQuestionIndex, freeNavigation, attemptFinalizedRef]);

  // QuizPlayer マウント時に body にフラグクラスを付与
  useEffect(() => {
    document.body.classList.add('quiz-playing');
    return () => {
      document.body.classList.remove('quiz-playing');
    };
  }, []);

  // 質問がない場合の表示
  if (questions.length === 0) {
    return (
      <>
        <div className="fixed inset-0 bg-white flex flex-col z-50">
          <ResponsiveContainer variant="quiz" className="flex-1">
          <ResponsiveHeader
            title={contentDetails?.title}
            onClose={handleClose}
            enableResponsive={true}
          />
          
          <div className="text-center py-8">
            <p className="text-gray-600">このコンテンツにはクイズがありません。</p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              戻る
            </button>
          </div>
        </ResponsiveContainer>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-white flex flex-col overflow-y-auto z-50">
      {/* フローティングデバッグトグルボタン */}
      <DebugToggle 
        onToggle={setDebugMode}
        position="top-right"
      />
      
      <ResponsiveContainer variant="quiz" className="flex-1">
        {/* ヘッダー - レスポンシブ版 */}
        <ResponsiveHeader
          title={contentDetails?.title}
          onClose={handleClose}
          timerComponent={
            <TimerDisplay 
              timeRemaining={timeRemaining || 0}
              isActive={isTimerActive || false}
              size="default"
            />
          }
          enableResponsive={true}
        />

        {/* プログレスバー */}
        <QuizProgress 
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
        />

        {/* デバッグ情報 - 条件付き表示（フローティングボタン制御対応） */}
        <DebugPanel 
          title="🔍 QuizPlayer デバッグ情報"
          forceShow={debugMode}
          data={{
            '問題': `${currentQuestionIndex + 1}/${questions.length}`,
            '移動': freeNavigation ? '自由' : '一方向',
            '表示': answerRevealMode === 'immediate' ? '即時表示' : '最後まとめ表示',
            '制限': `${contentDetails?.timeLimitPerQuestion || 0}秒/問`,
            '回答済み': currentAnswered ? 'Yes' : 'No',
            '時間切れ': isTimeUp ? 'Yes' : 'No',
            'セッション回答数': Object.keys(sessionResponses).length,
            '全問回答完了': (questions.length > 0 && questions.every(q => sessionResponses[q.id])) ? 'Yes' : 'No',
            '結果表示中': showFinalSummary ? 'Yes' : 'No',
            '現在の問題ID': currentQuestion?.id || 'N/A',
            '🚀 自動進行条件': (() => {
              const quickAnswerMode = contentDetails?.quickAnswerMode || 'standard';
              const shouldAutoProgress = (
                answerRevealMode === 'end' &&
                quickAnswerMode === 'quick' &&
                !freeNavigation &&
                currentQuestionIndex < questions.length - 1 &&
                !isTimeUp
              );
              return shouldAutoProgress ? 'Yes (回答後自動進行)' : 'No';
            })()
          }}
        />

        {/* 質問レンダリング */}
        <div className="mb-6">
          {/* 時間切れ表示 - 視覚的強化版 */}
          {isTimeUp && (
            <TimeUpAlert
              answerRevealMode={answerRevealMode}
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={questions.length}
              freeNavigation={freeNavigation}
            />
          )}
          
          <QuestionRenderer 
            question={currentQuestion}
            currentQuestionIndex={currentQuestionIndex}
            answered={currentAnswered}
            isTimeUp={isTimeUp}
            onSubmit={handleQuestionSubmitWrapper}
            contentDetails={contentDetails}
          />
        </div>

        {/* フィードバック表示 */}
        {showFeedback && currentQuestion && (
          <QuizFeedback 
            response={sessionResponses[currentQuestion.id]} 
          />
        )}

        {/* 最終まとめ表示 */}
        {showFinalSummary && (
          <QuizResultsSummary
            sessionResponses={sessionResponses}
            questions={questions}
            startTime={startTime}
            endTime={endTime}
            onClose={handleClose}
          />
        )}

        {/* ナビゲーションバー - レスポンシブ版 */}
        {questions.length > 0 && !showFinalSummary && (
          <div className="mt-8">
            <ResponsiveNavigation 
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={questions.length}
              freeNavigation={freeNavigation}
              sessionResponses={sessionResponses}
              questions={questions}
              isTimeUp={isTimeUp}
              quickAnswerMode={contentDetails?.quickAnswerMode || 'standard'}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onNavigateTo={handleNavigateTo}
              onShowResults={handleShowResults}
              enableResponsive={true}
            />
            {/* NavigationBar デバッグ情報 - 条件付き表示（フローティングボタン制御対応） */}
            <DebugPanel 
              title="📍 NavigationBar デバッグ"
              className="mt-2 p-2 bg-yellow-100 rounded text-xs"
              forceShow={debugMode}
              data={{
                '全問回答完了': questions.every(q => sessionResponses[q.id]) ? 'Yes' : 'No',
                '現在問題が最後': (currentQuestionIndex === questions.length - 1) ? 'Yes' : 'No',
                '現在問題回答済み': sessionResponses[questions[currentQuestionIndex]?.id] ? 'Yes' : 'No',
                '移動モード': freeNavigation ? '自由移動' : '一方向移動',
                'quickAnswerMode': contentDetails?.quickAnswerMode || 'standard',
                '結果ボタン表示条件（設定ベース）': (() => {
                  const quickAnswerMode = contentDetails?.quickAnswerMode || 'standard';
                  const allAnswered = questions.every(q => sessionResponses[q.id]);
                  const isLastQuestion = currentQuestionIndex === questions.length - 1;
                  const hasCurrentAnswer = sessionResponses[questions[currentQuestionIndex]?.id];
                  
                  if (quickAnswerMode === 'quick') {
                    return 'No (クイック回答モードで自動遷移)';
                  }
                  
                  if (freeNavigation) {
                    return allAnswered ? 'Yes (全問完了)' : 'No';
                  } else {
                    return (isLastQuestion && hasCurrentAnswer) ? 'Yes (最後問題+回答済み)' : 'No';
                  }
                })()
              }}
            />
          </div>
        )}
      </ResponsiveContainer>
      </div>
    </>
  );
};

QuizPlayer.propTypes = {
  modalContentId: PropTypes.string.isRequired,
  activeSubject: PropTypes.string.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  studentId: PropTypes.string.isRequired,
  contentDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default QuizPlayer; 